import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, Edit3, X, AlertCircle, Check, ArrowRight, Calendar as CalendarIcon } from 'lucide-react';
import { useTimezone } from '../contexts/TimezoneContext';
import { useLanguage } from '../contexts/LanguageContext';
import { safeJson } from '../utils/api';
import { parseBackendDate } from '../utils/formatUtils';
import Pagination from './Pagination';

const CalendarPage = () => {
    const navigate = useNavigate();
    const { formatDateTime, formatDate, formatTime } = useTimezone();
    const { t, tt } = useLanguage();
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
    const [dateTo, setDateTo] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 7);
        return tomorrow.toISOString().split('T')[0];
    });
    const [viewMode, setViewMode] = useState('all'); // 'day', 'week', 'month', 'range', 'all'
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = new to old, 'asc' = old to new
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingReminder, setEditingReminder] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'completed', 'cancelled'
    const [managerFilter, setManagerFilter] = useState('all'); // 'all' or manager ID
    const [managers, setManagers] = useState([]);
    // [CLEANUP-2026-05-21] Cleanup modal state (superadmin only)
    const [showCleanupModal, setShowCleanupModal] = useState(false);
    const [cleanupDays, setCleanupDays] = useState(45);
    const [cleanupStatuses, setCleanupStatuses] = useState({ completed: true, cancelled: true, pending: false });
    const [cleanupResult, setCleanupResult] = useState(null);
    const [cleanupLoading, setCleanupLoading] = useState(false);

    const runCleanup = async (dryRun) => {
        const statuses = Object.entries(cleanupStatuses).filter(([, v]) => v).map(([k]) => k).join(',');
        if (!statuses) {
            setError(tt('Выберите хотя бы один статус для удаления'));
            return;
        }
        if (cleanupDays < 7) {
            setError(tt('Минимальный возраст для удаления — 7 дней'));
            return;
        }
        if (!dryRun && !window.confirm(`${tt('Удалить напоминания старше')} ${cleanupDays} ${tt('дней')}? ${tt('Это действие нельзя отменить.')}`)) {
            return;
        }
        setCleanupLoading(true);
        setCleanupResult(null);
        try {
            const token = localStorage.getItem('token');
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/reminders/cleanup?days=${cleanupDays}&statuses=${encodeURIComponent(statuses)}&dry_run=${dryRun}`;
            const resp = await fetch(url, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await safeJson(resp);
            if (!resp.ok) {
                setError(data?.detail || tt('Ошибка очистки'));
            } else {
                setCleanupResult(data);
                if (!dryRun) {
                    fetchAllReminders();
                }
            }
        } catch (e) {
            setError(tt('Ошибка очистки'));
        } finally {
            setCleanupLoading(false);
        }
    };

    // Получение текущего пользователя
    useEffect(() => {
        fetchCurrentUser();
    }, []);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [viewMode, statusFilter, managerFilter, dateFrom, dateTo]);


    const fetchCurrentUser = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const userData = await safeJson(response);
                setCurrentUser(userData);
            } else {
                setError('Ошибка при получении данных пользователя');
            }
        } catch (err) {
            console.error('Ошибка при получении пользователя:', err);
            setError('Ошибка при загрузке данных пользователя');
        }
    };

    const fetchManagers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const usersData = await safeJson(response);
                // Фильтруем только сотрудников (исключаем клиентов)
                const staffOnly = usersData.filter(user => 
                    user.role !== 'client' && user.role !== undefined
                );
                console.log('📝 CalendarPage: Filtered managers:', staffOnly.map(m => ({ id: m.id, name: m.name, role: m.role })));
                setManagers(staffOnly);
            } else {
                console.error('Не удалось получить список менеджеров');
            }
        } catch (err) {
            console.error('Ошибка при получении менеджеров:', err);
        }
    };

    // Получение всех напоминаний через оптимизированный эндпоинт
    useEffect(() => {
        if (currentUser) {
            fetchAllReminders();
            fetchManagers();
        }
    }, [currentUser]);

    const fetchAllReminders = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            
            // Используем оптимизированный эндпоинт для получения всех напоминаний
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reminders`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Не удалось получить список напоминаний');
            }

            const allReminders = await safeJson(response);
            setReminders(allReminders); // Фильтрация уже выполнена на бэкенде
        } catch (err) {
            console.error('Ошибка при загрузке напоминаний:', err);
            setError('Ошибка при загрузке напоминаний');
        } finally {
            setLoading(false);
        }
    };

    // Удаляем функцию filterRemindersByRole, так как фильтрация теперь на бэкенде

    // Получение и сортировка напоминаний для отображения
    const getSortedReminders = () => {
        let filteredReminders = [...reminders];
        
        // Фильтрация по периоду времени
        // [NOTIF-2026-05-22] parseBackendDate чтобы legacy naive-ISO не сдвигались на browser-tz.
        if (viewMode === 'day') {
            const selected = new Date(selectedDate);
            filteredReminders = filteredReminders.filter(reminder => {
                const reminderDate = parseBackendDate(reminder.scheduled_time);
                return reminderDate.toDateString() === selected.toDateString();
            });
        } else if (viewMode === 'week') {
            const selected = new Date(selectedDate);
            const startOfWeek = new Date(selected);
            startOfWeek.setDate(selected.getDate() - selected.getDay());
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            
            filteredReminders = filteredReminders.filter(reminder => {
                const reminderDate = parseBackendDate(reminder.scheduled_time);
                return reminderDate >= startOfWeek && reminderDate <= endOfWeek;
            });
        } else if (viewMode === 'month') {
            const selected = new Date(selectedDate);
            filteredReminders = filteredReminders.filter(reminder => {
                const reminderDate = parseBackendDate(reminder.scheduled_time);
                return reminderDate.getFullYear() === selected.getFullYear() &&
                       reminderDate.getMonth() === selected.getMonth();
            });
        } else if (viewMode === 'range') {
            const fromDate = new Date(dateFrom);
            const toDate = new Date(dateTo);
            toDate.setHours(23, 59, 59);
            
            filteredReminders = filteredReminders.filter(reminder => {
                const reminderDate = parseBackendDate(reminder.scheduled_time);
                return reminderDate >= fromDate && reminderDate <= toDate;
            });
        }
        // If viewMode === 'all', show all reminders
        
        // Фильтрация по статусу
        if (statusFilter !== 'all') {
            filteredReminders = filteredReminders.filter(reminder => reminder.status === statusFilter);
        }
        
        // Фильтрация по менеджеру
        if (managerFilter !== 'all') {
            const beforeFilter = filteredReminders.length;
            filteredReminders = filteredReminders.filter(reminder => {
                // Compare using loose equality to handle string/number type mismatch
                return String(reminder.created_by) === String(managerFilter);
            });
            console.log(`📝 CalendarPage: Manager filter applied. Manager ID: ${managerFilter}, Before: ${beforeFilter}, After: ${filteredReminders.length}`);
            if (filteredReminders.length > 0) {
                console.log('📝 CalendarPage: Sample filtered reminder created_by:', filteredReminders[0].created_by);
            }
        }
        
        // Сортировка
        return filteredReminders.sort((a, b) => {
            const dateA = parseBackendDate(a.scheduled_time);
            const dateB = parseBackendDate(b.scheduled_time);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
    };

    // Пагинация
    const sortedReminders = getSortedReminders();
    const totalPages = Math.ceil(sortedReminders.length / pageSize);
    const paginatedReminders = sortedReminders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    // Обновление статуса напоминания
    const updateReminderStatus = async (reminderId, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reminders/${reminderId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: newStatus }),
            });

            if (response.ok) {
                // Обновляем локальное состояние
                const updatedReminders = reminders.map(r => 
                    r.id === reminderId ? { ...r, status: newStatus } : r
                );
                setReminders(updatedReminders);
            } else {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                setError('Ошибка при обновлении статуса напоминания');
            }
        } catch (err) {
            console.error('Ошибка при обновлении напоминания:', err);
            setError('Ошибка при обновлении напоминания');
        }
    };

    // Редактирование напоминания
    const handleEditReminder = (reminder) => {
        setEditingReminder(reminder);
        setShowEditModal(true);
    };

    // Сохранение изменений напоминания
    const handleSaveReminder = async (updatedData) => {
        try {
            const token = localStorage.getItem('token');
            
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reminders/${editingReminder.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedData),
            });

            if (response.ok) {
                const updatedReminder = await safeJson(response);
                const updatedReminders = reminders.map(r => 
                    r.id === editingReminder.id ? updatedReminder : r
                );
                setReminders(updatedReminders);
                setShowEditModal(false);
                setEditingReminder(null);
            } else {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                setError('Ошибка при сохранении изменений');
            }
        } catch (err) {
            console.error('Ошибка при сохранении напоминания:', err);
            setError('Ошибка при сохранении изменений');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'text-yellow-600 bg-yellow-100';
            case 'completed':
                return 'text-green-600 bg-green-100';
            case 'cancelled':
                return 'text-red-600 bg-red-100';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'pending':
                return tt('Ожидает');
            case 'completed':
                return tt('Выполнено');
            case 'cancelled':
                return tt('Отменено');
            default:
                return status;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <span className="ml-3 text-gray-600">{tt('Загрузка календаря...')}</span>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center">
                        <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600 mr-2 sm:mr-3" />
                        <div>
                            <h1 className="page-title">{tt('Календарь напоминаний')}</h1>
                            <p className="page-subtitle">{tt('Управление запланированными действиями')}</p>
                        </div>
                    </div>
                    {/* [CLEANUP-2026-05-21] Superadmin-only cleanup button */}
                    {currentUser?.role === 'superadmin' && (
                        <button
                            onClick={() => setShowCleanupModal(true)}
                            data-testid="reminders-cleanup-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-900 text-sm font-medium border border-amber-300 transition"
                            title="Удалить старые напоминания"
                        >
                            <X className="w-4 h-4" />
                            {tt('Чистка календаря')}
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Фильтры и навигация */}
            <div className="bg-white shadow rounded-lg mb-6">
                <div className="px-2 sm:px-6 py-4 border-b border-gray-200">
                    <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
                        <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:space-x-4 w-full sm:w-auto">
                            {/* Period Filter */}
                            <div>
                                <label className="form-label">{tt('Период')}</label>
                                <select
                                    value={viewMode}
                                    onChange={(e) => setViewMode(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="all">{tt('Все напоминания')}</option>
                                    <option value="day">{tt('День')}</option>
                                    <option value="week">{tt('Неделя')}</option>
                                    <option value="month">{tt('Месяц')}</option>
                                    <option value="range">{tt('Диапазон дат')}</option>
                                </select>
                            </div>
                            
                            {/* Sort Order */}
                            <div>
                                <label className="form-label">{tt('Сортировка')}</label>
                                <select
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="desc">{tt('От новых к старым')}</option>
                                    <option value="asc">{tt('От старых к новым')}</option>
                                </select>
                            </div>
                            
                            {/* Page Size */}
                            <div>
                                <label className="form-label">{tt('Показывать')}</label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(parseInt(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="form-select"
                                >
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            
                            {/* Status Filter */}
                            <div>
                                <label className="form-label">{tt('Статус')}</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="form-select"
                                >
                                    <option value="all">{tt('Все статусы')}</option>
                                    <option value="pending">{tt('Ожидает')}</option>
                                    <option value="completed">{tt('Выполнено')}</option>
                                    <option value="cancelled">{tt('Отменено')}</option>
                                </select>
                            </div>
                            
                            {/* Manager Filter */}
                            <div>
                                <label className="form-label">{tt('Менеджер')}</label>
                                <select
                                    value={managerFilter}
                                    onChange={(e) => {
                                        setManagerFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="form-select text-gray-900"
                                    style={{ color: '#111827' }}
                                >
                                    <option value="all" style={{ color: '#111827' }}>{tt('Все менеджеры')}</option>
                                    {managers.map(manager => (
                                        <option key={manager.id} value={manager.id} style={{ color: '#111827' }}>
                                            {manager.name || manager.full_name || `User ${manager.id}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            {/* Clear Filters Button */}
                            <div className="flex items-end">
                                <button
                                    onClick={() => {
                                        setStatusFilter('all');
                                        setManagerFilter('all');
                                        setViewMode('all');
                                        setDateFrom(new Date().toISOString().split('T')[0]);
                                        const nextWeek = new Date();
                                        nextWeek.setDate(nextWeek.getDate() + 7);
                                        setDateTo(nextWeek.toISOString().split('T')[0]);
                                        setSelectedDate(new Date().toISOString().split('T')[0]);
                                        setCurrentPage(1);
                                    }}
                                    className="btn btn-secondary h-10 px-4 text-sm"
                                    title={tt('Очистить все фильтры')}
                                >
                                    <X className="w-4 h-4 mr-2" />
                                    {tt('Очистить фильтры')}
                                </button>
                            </div>
                            
                            {/* Date Filters for specific periods */}
                            {viewMode === 'range' ? (
                                <>
                                    <div>
                                        <label className="form-label">{tt('Дата от')}</label>
                                        <input
                                            type="date"
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">{tt('Дата до')}</label>
                                        <input
                                            type="date"
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            className="form-input"
                                        />
                                    </div>
                                </>
                            ) : viewMode !== 'all' && (
                                <div>
                                    <label className="form-label">{tt('Дата')}</label>
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        className="form-input"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="text-sm text-gray-600">
                            {tt('Показано')}: {paginatedReminders.length} {tt('из')} {sortedReminders.length} {tt('напоминаний')} ({tt('страница')} {currentPage} {tt('из')} {totalPages})
                        </div>
                    </div>
                </div>
            </div>

            {/* Список напоминаний */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900">
                        {tt('Напоминания на')} {
                            viewMode === 'day' ? tt('день') : 
                            viewMode === 'week' ? tt('неделю') : 
                            viewMode === 'month' ? tt('месяц') :
                            tt('выбранный период')
                        }
                    </h2>
                </div>

                <div className="divide-y divide-gray-200">
                    {paginatedReminders.length === 0 ? (
                        <div className="px-6 py-12 text-center">
                            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">{tt('Нет напоминаний')}</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {tt('На выбранный период напоминания не найдены.')}
                            </p>
                        </div>
                    ) : (
                        paginatedReminders.map((reminder) => (
                            <div 
                                key={reminder.id} 
                                className="px-6 py-4 hover:bg-gray-50 cursor-pointer border-l-4 border-l-blue-400"
                                onClick={() => navigate(`/crm/clients/${reminder.client_id}`)}
                                title={tt('Перейти к карточке клиента')}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center mb-2">
                                            <h3 className="text-lg font-medium text-gray-900 mr-3">
                                                {reminder.title}
                                            </h3>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(reminder.status)}`}>
                                                {getStatusText(reminder.status)}
                                            </span>
                                        </div>
                                        
                                        <div className="flex items-center text-sm text-gray-600 mb-2">
                                            <Clock className="h-4 w-4 mr-1" />
                                            <span className="mr-4">
                                                {formatDate(reminder.scheduled_time)} {tt('в')} {formatTime(reminder.scheduled_time)}
                                            </span>
                                            <User className="h-4 w-4 mr-1" />
                                            <span>{tt('Клиент')}: {reminder.client_name}</span>
                                        </div>

                                        {reminder.description && (
                                            <p className="text-sm text-gray-700 mb-2">
                                                {reminder.description}
                                            </p>
                                        )}

                                        <div className="text-xs text-gray-500">
                                            {tt('Создал')}: {reminder.created_by_name}
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2 ml-4">
                                        {reminder.status === 'pending' && (
                                            <>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        updateReminderStatus(reminder.id, 'completed');
                                                    }}
                                                    className="p-2 text-green-600 hover:bg-green-50 rounded-full"
                                                    title={tt('Отметить как выполненное')}
                                                >
                                                    <Check className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditReminder(reminder);
                                                    }}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                                                    title={tt('Редактировать')}
                                                >
                                                    <Edit3 className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        updateReminderStatus(reminder.id, 'cancelled');
                                                    }}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                                                    title={tt('Отменить')}
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </>
                                        )}
                                        
                                        {reminder.status !== 'pending' && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditReminder(reminder);
                                                }}
                                                className="p-2 text-gray-600 hover:bg-gray-50 rounded-full"
                                                title={tt('Редактировать')}
                                            >
                                                <Edit3 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalItems={sortedReminders.length}
                itemsPerPage={pageSize}
                onPageChange={setCurrentPage}
            />

            {/* Модальное окно редактирования */}
            {showEditModal && editingReminder && (
                <EditReminderModal
                    reminder={editingReminder}
                    onSave={handleSaveReminder}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingReminder(null);
                    }}
                />
            )}

            {/* [CLEANUP-2026-05-21] Superadmin cleanup modal */}
            {showCleanupModal && currentUser?.role === 'superadmin' && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-16 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" data-testid="cleanup-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900">{tt('Чистка календаря')}</h3>
                            <button onClick={() => { setShowCleanupModal(false); setCleanupResult(null); }} className="text-gray-400 hover:text-gray-600">
                                <X className="h-6 w-6" />
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">
                            {tt('Удаляет старые напоминания, чтобы база не разрасталась. Минимум 7 дней.')}
                        </p>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{tt('Старше скольких дней удалять')}</label>
                                <div className="flex gap-2">
                                    {[30, 45, 60, 90, 180].map(d => (
                                        <button key={d} type="button" onClick={() => setCleanupDays(d)}
                                            className={`flex-1 py-1.5 text-xs rounded border ${cleanupDays === d ? 'bg-amber-500 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                                            {d}
                                        </button>
                                    ))}
                                </div>
                                <input type="number" min={7} value={cleanupDays} onChange={(e) => setCleanupDays(parseInt(e.target.value) || 7)}
                                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-testid="cleanup-days-input"/>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{tt('Какие статусы удалять')}</label>
                                <div className="space-y-1">
                                    {[
                                        { k: 'completed', l: 'Выполненные' },
                                        { k: 'cancelled', l: 'Отменённые' },
                                        { k: 'pending', l: 'Ожидающие (внимание!)' },
                                    ].map(({ k, l }) => (
                                        <label key={k} className="flex items-center gap-2 text-sm">
                                            <input type="checkbox" checked={cleanupStatuses[k]}
                                                onChange={(e) => setCleanupStatuses({ ...cleanupStatuses, [k]: e.target.checked })}
                                                data-testid={`cleanup-status-${k}`}/>
                                            <span>{tt(l)}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {cleanupResult && (
                                <div className={`p-2 rounded text-sm ${cleanupResult.deleted > 0 ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'}`} data-testid="cleanup-result">
                                    {cleanupResult.dry_run
                                        ? `${tt('Найдено')} ${cleanupResult.matched} ${tt('напоминаний для удаления')}`
                                        : `${tt('Удалено')} ${cleanupResult.deleted} ${tt('напоминаний')}`}
                                </div>
                            )}
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => runCleanup(true)} disabled={cleanupLoading}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
                                    data-testid="cleanup-dry-run-btn">
                                    {tt('Посчитать')}
                                </button>
                                <button type="button" onClick={() => runCleanup(false)} disabled={cleanupLoading}
                                    className="flex-1 px-3 py-2 border border-red-300 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
                                    data-testid="cleanup-confirm-btn">
                                    {cleanupLoading ? tt('Работаю…') : tt('Удалить')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Компонент модального окна редактирования напоминания
const EditReminderModal = ({ reminder, onSave, onClose }) => {
    const { tt } = useLanguage();
    // [NOTIF-2026-05-20] selectedTimezone is forced to UTC now (see
    // TimezoneContext) — kept import-free here since time math is trivial.
    
    // [NOTIF-2026-05-22] UTC ISO → browser local "YYYY-MM-DDTHH:MM" для datetime-local.
    // Через parseBackendDate чтобы безопасно обработать legacy naive-ISO без `Z`.
    const getLocalDateTime = (utcDateTime) => {
        if (!utcDateTime) return '';
        const d = parseBackendDate(utcDateTime);
        if (!d || isNaN(d.getTime())) return '';
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    // [NOTIF-2026-05-21] datetime-local (browser local) → UTC ISO для отправки.
    const getUtcDateTime = (localDateTimeString) => {
        if (!localDateTimeString) return '';
        return new Date(localDateTimeString).toISOString();
    };

    const [formData, setFormData] = useState({
        title: reminder.title,
        description: reminder.description || '',
        scheduled_time: getLocalDateTime(reminder.scheduled_time),
        status: reminder.status
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({
            ...formData,
            scheduled_time: getUtcDateTime(formData.scheduled_time)
        });
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                            {tt('Редактировать напоминание')}
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tt('Заголовок')}
                            </label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tt('Описание')}
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                                rows={3}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tt('Дата и время')}
                            </label>
                            <input
                                type="datetime-local"
                                value={formData.scheduled_time}
                                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tt('Статус')}
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                                <option value="pending">{tt('Ожидает')}</option>
                                <option value="completed">{tt('Выполнено')}</option>
                                <option value="cancelled">{tt('Отменено')}</option>
                            </select>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                            >
                                {tt('Отмена')}
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                            >
                                {tt('Сохранить')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CalendarPage;